import React, { useEffect, useMemo, useState } from "react";
import { pickPortfolio } from "./selectedPortfolio";
import { useNavigate, useParams } from "react-router-dom";
import { Lock, Loader2, ArrowLeft, Paperclip } from "lucide-react";
import { API } from "./shared";
import { SCHOOL_COLOR } from "./portfolioFlow";
import { ScoringComparisonCard } from "@/components/ScoringComparisonCard";

const FREQ = { annual: "Annual", quarterly: "Quarterly", monthly: "Monthly", biweekly: "Biweekly", weekly: "Weekly" };

const answerText = (ind, r) => {
  if (!r) return "Not answered yet";
  if (r.is_not_applicable) return "Not applicable";
  const v = r.raw_value;
  if (v === null || v === undefined || v === "") return "Not answered yet";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  const s = String(v);
  if (ind.input_type === "yes_no") return /^(yes|true|y|1)$/i.test(s) ? "Yes" : /^(no|false|n|0)$/i.test(s) ? "No" : s;
  if (FREQ[s]) return FREQ[s];
  return ind.unit && !/^(Y\/N|text|#|scale)$/i.test(ind.unit) ? `${s} ${ind.unit}` : s;
};

// Read-only school diagnostic for Portfolio users: real indicators, no inputs.
export default function SchoolDiagnosticView() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: "" });
  const [diags, setDiags] = useState([]);
  const [sel, setSel] = useState("");
  const [qs, setQs] = useState(null);
  const [v1, setV1] = useState(null);
  const [token, setToken] = useState("");

  useEffect(() => {
    let tok = "";
    (async () => {
      try {
        const me = await (await fetch(`${API}/api/portfolio/me`, { credentials: "include" })).json();
        const pid = pickPortfolio(me.portfolios)?.id;
        const r = await fetch(`${API}/api/portfolio/${pid}/schools/${tenantId}/open`, { method: "POST", credentials: "include" });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || "Couldn't open this school's diagnostic");
        tok = d.session_token; setToken(tok);
        const h = { "x-session-token": tok };
        const [list, q] = await Promise.all([
          fetch(`${API}/api/diagnostics`, { headers: h }).then((x) => x.json()),
          fetch(`${API}/api/diagnostics/question-set/active`, { headers: h }).then((x) => x.json()),
        ]);
        const rows = (Array.isArray(list) ? list : list.diagnostics || []).filter((x) => x.perspective !== "portfolio" && !x.hidden_from_portfolio)
          .sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
        const want = new URLSearchParams(window.location.search).get("diag");
        setDiags(rows); setQs(q); setSel((rows.find((x) => x.id === want) || rows[0])?.id || "");
        setState({ loading: false, error: "", school: d.tenant?.school_name || d.tenant?.name });
      } catch (e) { setState({ loading: false, error: e.message }); }
    })();
    return () => {
      if (tok) fetch(`${API}/api/portfolio/school-session/close`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_token: tok }) }).catch(() => {});
    };
  }, [tenantId]);

  useEffect(() => {
    if (!sel || !token) return;
    setV1(null);
    fetch(`${API}/api/diagnostics/${sel}/v1`, { headers: { "x-session-token": token } }).then((x) => x.json()).then(setV1).catch(() => {});
  }, [sel, token]);

  const byInd = useMemo(() => Object.fromEntries((v1?.responses || []).map((r) => [r.indicator_id, r])), [v1]);

  // ?pillar=<id> (from a heat-map card) scrolls to that pillar once loaded.
  useEffect(() => {
    const pid = new URLSearchParams(window.location.search).get("pillar");
    if (v1 && pid) setTimeout(() => document.querySelector(`[data-testid="school-view-pillar-${pid}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
  }, [v1]);

  if (state.loading) return <div className="py-16 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div>;
  if (state.error) return <div className="py-16 text-center text-sm text-red-600" data-testid="school-view-error">{state.error}</div>;

  return (
    <div data-testid="school-diagnostic-view" className="space-y-4">
      <button onClick={() => navigate(`/portfolio/schools/${tenantId}`)} className="inline-flex items-center gap-1.5 py-2 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> {state.school || "School"}
      </button>
      <div className="sticky top-0 z-10 rounded-lg border border-[#bfe3ee] bg-[#e6f4f8] px-4 py-2.5 text-sm font-semibold text-[#00607d] inline-flex w-full items-center gap-2" data-testid="school-view-banner">
        <Lock className="h-4 w-4 shrink-0" /> This is the school's own diagnostic. Only the school can change it.
      </div>
      <div className="rounded-xl overflow-hidden border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-white" style={{ background: SCHOOL_COLOR }} data-testid="school-view-header">
          <span className="font-semibold inline-flex items-center gap-2"><Lock className="h-4 w-4" /> School's Diagnostic · Submitted by the school · View only</span>
          {v1?.scoring_version && <span className="text-xs text-white/90" data-testid="school-view-scoring-version">Scoring v{v1.scoring_version}</span>}
          {diags.length > 1 && (
            <select value={sel} onChange={(e) => setSel(e.target.value)} data-testid="school-view-period" className="rounded-md bg-white/95 px-2 py-1.5 text-sm text-slate-800">
              {diags.map((x) => <option key={x.id} value={x.id}>{x.period_label || x.diagnostic_name}</option>)}
            </select>
          )}
        </div>
        {!diags.length && <p className="p-6 text-sm text-slate-500" data-testid="school-view-empty">The school hasn't started a diagnostic yet.</p>}
      </div>
      {v1?.version_comparison && (
        <ScoringComparisonCard comparison={v1.version_comparison}
          onJump={(id) => {
            const el = document.querySelector(`[data-testid="school-view-ind-${id}"]`);
            el?.scrollIntoView({ behavior: "smooth", block: "center" });
            el?.classList.add("ring-2", "ring-[#475569]");
            setTimeout(() => el?.classList.remove("ring-2", "ring-[#475569]"), 2400);
          }} />
      )}
      {v1 && (qs?.pillars || []).map((p) => (
        <section key={p.id} className="scroll-mt-24 rounded-xl border border-slate-200 bg-white" data-testid={`school-view-pillar-${p.id}`}>
          <h2 className="border-b border-slate-100 px-4 py-3 font-semibold text-slate-900">{p.name}</h2>
          <ol className="divide-y divide-slate-100">
            {(qs.indicators || []).filter((i) => i.pillar_id === p.id).map((ind) => {
              const r = byInd[ind.id];
              return (
                <li key={ind.id} className="px-4 py-3 grid gap-1 md:grid-cols-[1fr_minmax(160px,40%)]" data-testid={`school-view-ind-${ind.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 break-words">{ind.indicator_name}</div>
                    {ind.what_to_enter && <div className="text-xs text-slate-500 break-words">{ind.what_to_enter}</div>}
                  </div>
                  <div className="text-sm text-slate-800 break-words">
                    <div className="font-semibold">{answerText(ind, r)}</div>
                    {r?.notes && <div className="mt-1 text-xs text-slate-600 whitespace-pre-wrap">{r.notes}</div>}
                    {r?.evidence_url && (
                      <a href={r.evidence_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-[#007a9e] underline break-all">
                        <Paperclip className="h-3 w-3" /> Evidence
                      </a>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </section>
      ))}
    </div>
  );
}
